import { useState } from 'react';

const fieldType = {
  email: {
    regex: /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
    message: 'Please enter a valid email address',
  },
  number: {
    regex: /^[0-9]*$/,
    message: 'Please enter a valid number',
  },
  strongPassword: {
    regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
    message:
      'Please enter a valid password, at least 8 characters, 1 uppercase, 1 lowercase, 1 number and 1 special character',
  },
};

export type FormFieldProps = {
  type?: 'email' | 'number' | 'strongPassword';
  message?: string;
  required?: boolean;
  isDirty?: (isDirty: boolean) => void;
  initialValue?: string;
};

const useFormField = ({
  type,
  required = false,
  message,
  initialValue = '',
  isDirty: isDirtyCallback,
}: FormFieldProps = {}) => {
  const [value, setValue] = useState(initialValue);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  function validate(value: string) {
    if (required && !value) {
      setErrorMessage(message ?? 'Please enter a value');
      return false;
    } else if (type && fieldType[type] && !fieldType[type].regex.test(value)) {
      setErrorMessage(message || fieldType[type].message);
      return false;
    } else {
      setErrorMessage('');
      return true;
    }
  }

  function onChange({
    target,
    value,
  }: React.ChangeEvent<HTMLInputElement> & {
    value: string;
  }) {
    const result = target?.value || value;
    setValue(result);

    if (isDirty) {
      if (!validate(result)) {
        setIsDirty(false);
      }
    }
  }

  return {
    onBlur: () => {
      validate(value);
      setIsDirty(true);
      if (isDirtyCallback) {
        isDirtyCallback(true);
      }
    },
    value,
    setValue,
    onChange,
    errorMessage,
    setErrorMessage,
    validate: () => validate(value),
    isDirty: value !== initialValue,
  };
};

export default useFormField;
